import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import KitchenLoader from './KitchenLoader.jsx'

// AI social-post generator for a promotion.
//
// Phases:
//   generating — calls generate-promo-posts Edge Function
//   review     — IG / FB / SMS / hashtags with copy-to-clipboard
//   error      — retry path
//
// The promotion + restaurant context are passed in via props. We
// auto-fire the generate call as soon as the modal opens (no inputs
// to collect — we already have everything from the promotion row).
export default function PromotionPostsModal({
  open,
  onClose,
  promotion,        // the row to generate from
  restaurant,       // { name, timezone }
}) {
  const [phase, setPhase]    = useState('generating')
  const [error, setError]    = useState(null)
  const [result, setResult]  = useState(null)
  const [copied, setCopied]  = useState({})  // { fieldKey: true } for "Copied!" feedback

  useEffect(() => {
    if (!open || !promotion) return
    setPhase('generating')
    setError(null)
    setResult(null)
    setCopied({})
    let cancelled = false
    ;(async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
        const { data: sess } = await supabase.auth.getSession()
        const token = sess?.session?.access_token || anonKey

        const payload = {
          promotion: {
            name:          promotion.name,
            description:   promotion.description,
            starts_on:     promotion.starts_on,
            ends_on:       promotion.ends_on,
            channels:      promotion.channels,
            cost_estimate: promotion.cost_estimate,
            style_notes:   promotion.style_notes,
          },
          restaurant: {
            name: restaurant?.name ?? null,
            timezone: restaurant?.timezone ?? null,
          },
        }

        const resp = await fetch(`${supabaseUrl}/functions/v1/generate-promo-posts`, {
          method: 'POST',
          headers: {
            'content-type':  'application/json',
            'authorization': `Bearer ${token}`,
            'apikey':        anonKey,
          },
          body: JSON.stringify(payload),
        })

        if (cancelled) return

        if (!resp.ok) {
          const errText = await resp.text()
          throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
        }
        const data = await resp.json()
        if (data.error) {
          setError(data.message || 'AI could not generate posts.')
          setPhase('error')
          return
        }
        setResult(data)
        setPhase('review')
      } catch (e) {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error('[PromotionPostsModal] generate failed', e)
        setError('Could not generate — please try again in a moment.')
        setPhase('error')
      }
    })()

    return () => { cancelled = true }
  }, [open, promotion, restaurant])

  if (!open) return null

  async function copyToClipboard(key, text) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied((prev) => ({ ...prev, [key]: true }))
      setTimeout(() => {
        setCopied((prev) => ({ ...prev, [key]: false }))
      }, 1500)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PromotionPostsModal] copy failed', e)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="AI promotion posts"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium truncate">
              AI Posts · {promotion?.name}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>
        </div>

        <div className="space-y-4 px-4 pb-4">
          {phase === 'generating' && (
            <KitchenLoader
              messages={[
                'Reading the promo\u2026',
                'Cooking up some captions\u2026',
                'Garnishing with hashtags\u2026',
                'Plating your posts\u2026',
              ]}
            />
          )}

          {phase === 'error' && (
            <div className="space-y-3">
              <div role="alert" className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
                {error}
              </div>
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  setPhase('generating')
                  // Retrigger by toggling — easiest: parent should
                  // close + reopen, but we can also re-run inline.
                  // The useEffect deps don't re-fire on phase change,
                  // so simplest path is to call the fetch again.
                  setResult(null)
                  setCopied({})
                  // Re-fetch by closing and reopening the same modal:
                  onClose?.()
                }}
                className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
              >
                Close and try again
              </button>
            </div>
          )}

          {phase === 'review' && result && (
            <>
              {result.notes && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm leading-relaxed text-emerald-900">
                  <div className="text-[11px] font-medium text-emerald-900/70">
                    AI's approach
                  </div>
                  <div className="mt-1">{result.notes}</div>
                </div>
              )}

              {result.warnings && result.warnings.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm leading-relaxed text-amber-900">
                  <div className="text-[11px] font-medium text-amber-900/70">
                    Heads up
                  </div>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <PostBlock
                label="Instagram caption"
                value={result.instagram_caption}
                copied={copied.instagram}
                onCopy={() => copyToClipboard('instagram', result.instagram_caption)}
              />

              <PostBlock
                label="Facebook post"
                value={result.facebook_post}
                copied={copied.facebook}
                onCopy={() => copyToClipboard('facebook', result.facebook_post)}
              />

              <PostBlock
                label={`SMS draft (${result.sms_draft?.length ?? 0} chars)`}
                value={result.sms_draft}
                copied={copied.sms}
                onCopy={() => copyToClipboard('sms', result.sms_draft)}
                hint="Replace {LINK} with your tracking URL or menu link before sending."
              />

              {result.hashtags && result.hashtags.length > 0 && (
                <PostBlock
                  label="Hashtags"
                  value={result.hashtags.join(' ')}
                  copied={copied.hashtags}
                  onCopy={() => copyToClipboard('hashtags', result.hashtags.join(' '))}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function PostBlock({ label, value, copied, onCopy, hint }) {
  if (!value) return null
  return (
    <div className="rounded-xl border border-black/10 bg-white p-3">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-medium text-black/60">
          {label}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className={`rounded-md px-2 py-1 text-xs font-medium ${
            copied
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-black/85">
        {value}
      </div>
      {hint && (
        <p className="mt-2 text-xs text-black/55">{hint}</p>
      )}
    </div>
  )
}
